import React from 'react';
import { useBranding } from 'modules/App/hooks/useBranding';

// Whitelabel preview switcher, rendered inside the ErrorTestingTools advanced
// panel (which owns the dev/QA visibility gating — this component only adds
// its own hard stops). Applies each tenant's registry color scheme through
// useBranding.setBrand, so the auth illustration accents, Clerk card theming,
// in-card logo, and the app chrome can be eyeballed per tenant without a
// dedicated build.
//
// Palettes mirror the backend registry (config/whitelabel-customers.json in
// the backend repo) as of 2026-11. The `tiba` registry id is a reusable demo
// slot on whitelabel.testcg.co.uk and currently demos **绿舟 GOING GREEN** (the
// o2b tenant); it was TIBA, then Scan Global Logistics, Dcycle, and the Chartered
// Institute of Export & International Trade. The superseded palettes are kept here for the same
// reason their assets are kept in public/whitelabel — a registry revert should be
// previewable. Local /whitelabel/* assets are preferred over the registry's
// absolute URLs so the toggle also works offline.

// Never in production builds, and never in whitelabel builds: a tenant's
// QA deployment must not expose other customers' brands. Build-time env, NOT
// the runtime store key — the switcher itself changes the store key, and
// gating on it would hide the switcher after the first click.
const IS_PROD_BUILD = process.env.REACT_APP_ENV_MODE === 'PROD';
const IS_WHITELABEL_BUILD =
  !!process.env.REACT_APP_WHITELABEL && process.env.REACT_APP_WHITELABEL !== 'carbonglance';

// Snapshot of the env-derived brand at module load, before any toggling —
// "CG" restores exactly what the build started with.
const { setBrand: _set, applyCss: _apply, ...DEFAULT_BRAND } = useBranding.getState();

const PRESETS = [
  { label: 'CG', brand: DEFAULT_BRAND },
  {
    label: 'Ayming',
    brand: {
      ...DEFAULT_BRAND,
      key: 'ayming',
      name: 'Ayming',
      primaryColor: '#00aeef',
      primaryColorDark: '#0079a7',
      secondaryColor: '#607489',
      logoUrl: 'https://www.ayming.com/wp-content/uploads/2025/07/Ayming.png',
      supportEmail: '',
    },
  },
  {
    label: 'Crowe',
    brand: {
      ...DEFAULT_BRAND,
      key: 'crowe',
      name: 'Crowe',
      primaryColor: '#011E41',
      primaryColorDark: '#001530',
      secondaryColor: '#011E41',
      logoUrl: '/whitelabel/crowe/crowe-logo-lockup.png',
      supportEmail: '',
    },
  },
  {
    // Brand Guidelines v01 (April 2026). Primary is Mid Green, not the logo's
    // Bright Green #0DE8C2: the guidelines forbid white text on Bright Green and
    // Bright Green on white, and the app's primary is used in exactly those ways.
    label: 'ChamberCustoms',
    brand: {
      ...DEFAULT_BRAND,
      key: 'chambercustoms',
      name: 'ChamberCustoms',
      primaryColor: '#065465',
      primaryColorDark: '#192634',
      secondaryColor: '#192634',
      secondaryColorLight: '#8C939A',
      secondaryColorLightest: '#EDEEEF',
      logoUrl: '/whitelabel/chambercustoms/chambercustoms-logo.png',
      faviconUrl: '/whitelabel/chambercustoms/favicon.png',
      fontFamily: 'Poppins',
      supportEmail: '',
    },
  },
  {
    label: 'Going Green',
    brand: {
      ...DEFAULT_BRAND,
      // Previews the production o2b tenant (the tiba slot currently mirrors it).
      key: 'o2b',
      name: '绿舟 GOING GREEN',
      primaryColor: '#176348',
      primaryColorDark: '#124F3A',
      secondaryColor: '#2B3229',
      secondaryColorLight: '#849087',
      secondaryColorLightest: '#F3F6F4',
      logoUrl: '/whitelabel/goinggreen/goinggreen-logo.png',
      supportEmail: '',
    },
  },
  {
    label: 'IoE&IT',
    brand: {
      ...DEFAULT_BRAND,
      key: 'tiba',
      name: 'Chartered Institute of Export & International Trade',
      primaryColor: '#1E1E5A',
      primaryColorDark: '#181848',
      secondaryColor: '#282828',
      secondaryColorLight: '#6C757D',
      secondaryColorLightest: '#EFEFEF',
      logoUrl: '/whitelabel/ioe/ioe-logo.png',
      supportEmail: '',
    },
  },
  {
    label: 'Dcycle',
    brand: {
      ...DEFAULT_BRAND,
      key: 'tiba',
      name: 'Dcycle',
      primaryColor: '#2947FE',
      primaryColorDark: '#1F37CC',
      secondaryColor: '#172467',
      secondaryColorLight: '#637084',
      secondaryColorLightest: '#F0F3FF',
      logoUrl: '/whitelabel/dcycle/dcycle-logo.png',
      supportEmail: '',
    },
  },
  {
    label: 'Scan Global',
    brand: {
      ...DEFAULT_BRAND,
      key: 'tiba',
      name: 'Scan Global Logistics',
      primaryColor: '#CD171D',
      primaryColorDark: '#A41217',
      secondaryColor: '#2E2E2E',
      secondaryColorLight: '#666666',
      secondaryColorLightest: '#F0F0F0',
      logoUrl: '/whitelabel/scangl/scangl-logo.png',
      supportEmail: '',
    },
  },
  {
    label: 'TIBA',
    brand: {
      ...DEFAULT_BRAND,
      key: 'tiba',
      name: 'TIBA',
      primaryColor: '#0068B2',
      primaryColorDark: '#004690',
      secondaryColor: '#1B2C3F',
      logoUrl: '/whitelabel/tiba/tiba-logo.png',
      supportEmail: '',
    },
  },
];

const BrandTestSwitcher = () => {
  const activeColor = useBranding((s) => s.primaryColor);
  const setBrand = useBranding((s) => s.setBrand);

  if (IS_PROD_BUILD || IS_WHITELABEL_BUILD) return null;

  return (
    <div className="mb-4">
      <p className="mb-2 text-xs font-semibold text-slate-700">Brand preview</p>
      <div className="flex flex-wrap gap-1">
        {PRESETS.map(({ label, brand }) => (
          <button
            key={label}
            type="button"
            onClick={() => setBrand(brand)}
            className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
              activeColor === brand.primaryColor
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/10"
              style={{ backgroundColor: brand.primaryColor }}
              aria-hidden="true"
            />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default BrandTestSwitcher;
